import React, { useMemo } from "react";

import { ElementState } from "@creatomate/preview";
import { Loader } from "@culturehq/components";
import { Trans } from "app/utils";
import { Thumbnail } from "chq";
import { useVideo } from "../ThumbnailsContext";

interface ThumbnailsProps {
  thumbnails: Thumbnail[];
  width: number;
  height: number;
  thumbnailsToShowCountFixed?: number;
}

interface VideoThumbnailsProps {
  containerHeight: number;
  containerWidth: number;
  element: ElementState;
  endTime: number;
  startTime: number;
  thumbnailsToShowCountFixed?: number;
}

const Thumbnails: React.FC<ThumbnailsProps> = React.memo(({
  thumbnails, width, height, thumbnailsToShowCountFixed
}) => (
  <div style={{ display: "flex", alignItems: "center", marginLeft: thumbnailsToShowCountFixed ? "0px" : "-30px" }}>
    {thumbnails.map((thumbnail, index) => (
      <div
        key={index}
        style={{
          width,
          height,
          backgroundImage: `url(${thumbnail.thumbnail})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "contain",
          backgroundPosition: "center"
        }}
      />
    ))}
  </div>
));

const VideoThumbnails: React.FC<VideoThumbnailsProps> = ({
  element,
  containerWidth,
  containerHeight,
  startTime,
  endTime,
  thumbnailsToShowCountFixed
}) => {
  const {
    MIN_THUMB_WIDTH,
    MAX_THUMB_WIDTH,
    PORTRAIT_THUMBS_PER_SECOND,
    portrait,
    getThumbnails
  } = useVideo();

  const thumbnails = useMemo(() => getThumbnails(element).slice(
    Math.floor(startTime),
    portrait ? Math.ceil(endTime) * PORTRAIT_THUMBS_PER_SECOND : Math.ceil(endTime) * 4
  ), [element, endTime, getThumbnails, portrait, PORTRAIT_THUMBS_PER_SECOND, startTime]);

  const { filteredThumbnails, thumbnailWidth } = useMemo(
    () => {
      const secondWidth = containerWidth / element.duration;
      const thumbnailWidth = Math.min(
        Math.max(secondWidth, MIN_THUMB_WIDTH),
        MAX_THUMB_WIDTH
      );
      const secondsPerThumb = portrait
        && secondWidth <= MIN_THUMB_WIDTH * PORTRAIT_THUMBS_PER_SECOND
        ? thumbnailWidth / secondWidth / PORTRAIT_THUMBS_PER_SECOND
        : thumbnailWidth / secondWidth;
      const totalThumbnails = portrait
        ? Math.ceil(containerWidth / thumbnailWidth) * PORTRAIT_THUMBS_PER_SECOND
        : Math.ceil(containerWidth / thumbnailWidth);
      const thumbnailsToShowCount = thumbnailsToShowCountFixed || totalThumbnails;

      const step = Math.max(Math.ceil(element.duration / thumbnailsToShowCount), 1);
      const filteredThumbnails = portrait
        ? thumbnails.filter(
          thumb => Math.round(thumb.timestamp % secondsPerThumb) === 0
        ) : thumbnails.filter((_, index) => index % step === 0);

      return {
        filteredThumbnails,
        thumbnailWidth: Math.min(
          Math.max(containerWidth / filteredThumbnails.length, MIN_THUMB_WIDTH),
          MAX_THUMB_WIDTH
        )
      };
    }, [
      element.duration,
      containerWidth,
      thumbnailsToShowCountFixed,
      MIN_THUMB_WIDTH,
      MAX_THUMB_WIDTH,
      portrait,
      PORTRAIT_THUMBS_PER_SECOND,
      thumbnails
    ]
  );

  if (filteredThumbnails.length === 0 && element.source.source.startsWith("https://platform")) {
    return (
      <div style={{ color: "white", display: "flex", alignItems: "center", marginLeft: "30px" }}>
        <Loader loading />
        <Trans>This video is being uploaded, please wait a few seconds</Trans>
      </div>
    );
  }

  return (
    <Thumbnails
      thumbnails={filteredThumbnails}
      width={thumbnailWidth}
      height={containerHeight}
      thumbnailsToShowCountFixed={thumbnailsToShowCountFixed}
    />
  );
};

export default VideoThumbnails;
